export const DISCONNECT_GRACE_MS = 30_000;

export type ControllerMode = "computer" | "human" | "grace" | "autopilot";

export interface ControllerAssignment {
  readonly seatId: string;
  readonly sessionId: string | null;
  readonly generation: number;
  readonly mode: ControllerMode;
  readonly disconnectedAtGameMs: number | null;
  readonly reclaimPending: boolean;
}

export interface PauseProposal {
  readonly paused: boolean;
  readonly requestedBy: string;
  readonly approvals: readonly string[];
}

export interface OnlineLifecycleState {
  readonly paused: boolean;
  readonly controllers: readonly ControllerAssignment[];
  readonly pauseProposal: PauseProposal | null;
}

export type OnlineLifecycleTransition =
  | {
      readonly kind: "controller-changed";
      readonly seatId: string;
      readonly sessionId: string;
      readonly generation: number;
      readonly mode: "human" | "autopilot";
    }
  | { readonly kind: "pause-changed"; readonly paused: boolean };

export function initialOnlineLifecycle(
  seatIds: readonly string[],
  humans: readonly { readonly seatId: string; readonly sessionId: string }[],
  generations: Readonly<Record<string, number>> = {},
): OnlineLifecycleState {
  const bySeat = new Map(humans.map((entry) => [entry.seatId, entry.sessionId]));
  if (bySeat.size !== humans.length || humans.some((entry) => !seatIds.includes(entry.seatId)))
    throw new TypeError("Invalid initial human controller assignments");
  if (new Set(humans.map((entry) => entry.sessionId)).size !== humans.length)
    throw new TypeError("A session cannot control multiple seats");
  return Object.freeze({
    paused: false,
    controllers: Object.freeze(
      seatIds.map((seatId) => {
        const sessionId = bySeat.get(seatId) ?? null;
        return Object.freeze({
          seatId,
          sessionId,
          generation: generations[seatId] ?? 0,
          mode: sessionId ? "human" : "computer",
          disconnectedAtGameMs: null,
          reclaimPending: false,
        });
      }),
    ),
    pauseProposal: null,
  });
}

export function validateOnlineLifecycle(value: unknown): OnlineLifecycleState {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new TypeError("Invalid online lifecycle state");
  const state = value as OnlineLifecycleState;
  if (typeof state.paused !== "boolean" || !Array.isArray(state.controllers))
    throw new TypeError("Invalid online lifecycle state");
  const seats = new Set<string>();
  const sessions = new Set<string>();
  for (const controller of state.controllers) {
    if (
      !controller ||
      typeof controller.seatId !== "string" ||
      !["computer", "human", "grace", "autopilot"].includes(controller.mode) ||
      !Number.isSafeInteger(controller.generation) ||
      controller.generation < 0 ||
      typeof controller.reclaimPending !== "boolean"
    )
      throw new TypeError("Invalid online controller assignment");
    if (seats.has(controller.seatId)) throw new TypeError("Duplicate online lifecycle seat");
    seats.add(controller.seatId);
    if (controller.sessionId !== null) {
      if (typeof controller.sessionId !== "string" || sessions.has(controller.sessionId))
        throw new TypeError("Invalid online lifecycle session");
      sessions.add(controller.sessionId);
    }
    if (
      controller.disconnectedAtGameMs !== null &&
      (!Number.isSafeInteger(controller.disconnectedAtGameMs) ||
        controller.disconnectedAtGameMs < 0)
    )
      throw new TypeError("Invalid lifecycle disconnect time");
    const hasSession = controller.sessionId !== null;
    const validShape =
      (controller.mode === "computer" &&
        !hasSession &&
        controller.disconnectedAtGameMs === null &&
        !controller.reclaimPending) ||
      (controller.mode === "human" &&
        hasSession &&
        controller.disconnectedAtGameMs === null &&
        !controller.reclaimPending) ||
      (controller.mode === "grace" && hasSession && controller.disconnectedAtGameMs !== null) ||
      (controller.mode === "autopilot" && hasSession && controller.disconnectedAtGameMs === null);
    if (!validShape) throw new TypeError("Contradictory online controller assignment");
  }
  validatePauseProposal(state.pauseProposal, sessions);
  return Object.freeze(structuredClone(state));
}

export function disconnectOnlineController(
  state: OnlineLifecycleState,
  sessionId: string,
  gameTimeMs: number,
): OnlineLifecycleState {
  safeTime(gameTimeMs);
  return mapController(state, sessionId, (controller) =>
    controller.mode !== "human"
      ? controller
      : {
          ...controller,
          mode: "grace",
          disconnectedAtGameMs: gameTimeMs,
          reclaimPending: false,
        },
  );
}

export function requestOnlineReclaim(
  state: OnlineLifecycleState,
  sessionId: string,
): OnlineLifecycleState {
  return mapController(state, sessionId, (controller) =>
    controller.mode === "grace" || controller.mode === "autopilot"
      ? { ...controller, reclaimPending: true }
      : controller,
  );
}

export function requestPauseChange(
  state: OnlineLifecycleState,
  sessionId: string,
  paused: boolean,
): OnlineLifecycleState {
  requireHumanSession(state, sessionId);
  if (state.paused === paused && !state.pauseProposal) return state;
  return Object.freeze({
    ...state,
    pauseProposal: Object.freeze({ paused, requestedBy: sessionId, approvals: [sessionId] }),
  });
}

export function approvePauseChange(
  state: OnlineLifecycleState,
  sessionId: string,
): OnlineLifecycleState {
  requireHumanSession(state, sessionId);
  const proposal = state.pauseProposal;
  if (!proposal) throw new TypeError("No pause change is awaiting approval");
  return Object.freeze({
    ...state,
    pauseProposal: Object.freeze({
      ...proposal,
      approvals: Object.freeze([...new Set([...proposal.approvals, sessionId])].sort()),
    }),
  });
}

export function advanceOnlineLifecycle(
  state: OnlineLifecycleState,
  gameTimeMs: number,
  connectedHumanSessions: readonly string[],
): {
  readonly state: OnlineLifecycleState;
  readonly transitions: readonly OnlineLifecycleTransition[];
} {
  safeTime(gameTimeMs);
  const connected = new Set(connectedHumanSessions);
  const transitions: OnlineLifecycleTransition[] = [];
  const controllers = state.controllers.map((controller) =>
    advanceController(controller, gameTimeMs, transitions),
  );
  const proposal = state.pauseProposal;
  const approved =
    proposal !== null &&
    [...connected].every((sessionId) => proposal.approvals.includes(sessionId));
  if (approved) transitions.push({ kind: "pause-changed", paused: proposal.paused });
  return Object.freeze({
    state: Object.freeze({
      paused: approved ? proposal.paused : state.paused,
      controllers: Object.freeze(controllers),
      pauseProposal: approved ? null : proposal,
    }),
    transitions: Object.freeze(transitions),
  });
}

function advanceController(
  controller: ControllerAssignment,
  gameTimeMs: number,
  transitions: OnlineLifecycleTransition[],
): ControllerAssignment {
  let mode: "human" | "autopilot" | undefined;
  if (controller.reclaimPending) mode = "human";
  else if (
    controller.mode === "grace" &&
    controller.disconnectedAtGameMs !== null &&
    gameTimeMs - controller.disconnectedAtGameMs >= DISCONNECT_GRACE_MS
  )
    mode = "autopilot";
  if (!mode || !controller.sessionId) return controller;
  const next = Object.freeze({
    ...controller,
    generation: controller.generation + 1,
    mode,
    disconnectedAtGameMs: null,
    reclaimPending: false,
  });
  transitions.push({
    kind: "controller-changed",
    seatId: next.seatId,
    sessionId: controller.sessionId,
    generation: next.generation,
    mode,
  });
  return next;
}

function mapController(
  state: OnlineLifecycleState,
  sessionId: string,
  change: (controller: ControllerAssignment) => ControllerAssignment,
): OnlineLifecycleState {
  let found = false;
  const controllers = state.controllers.map((controller) => {
    if (controller.sessionId !== sessionId) return controller;
    found = true;
    return Object.freeze(change(controller));
  });
  if (!found) throw new TypeError("Session has no persistent controller assignment");
  return Object.freeze({ ...state, controllers: Object.freeze(controllers) });
}

function requireHumanSession(state: OnlineLifecycleState, sessionId: string): void {
  if (!state.controllers.some((entry) => entry.sessionId === sessionId))
    throw new TypeError("Session has no human controller vote");
}

function safeTime(value: number): void {
  if (!Number.isSafeInteger(value) || value < 0) throw new TypeError("Invalid lifecycle game time");
}

function validatePauseProposal(value: PauseProposal | null, sessions: ReadonlySet<string>): void {
  if (value === null) return;
  if (
    !value ||
    typeof value.paused !== "boolean" ||
    typeof value.requestedBy !== "string" ||
    !sessions.has(value.requestedBy) ||
    !Array.isArray(value.approvals) ||
    value.approvals.length !== new Set(value.approvals).size ||
    !value.approvals.includes(value.requestedBy) ||
    value.approvals.some((sessionId) => typeof sessionId !== "string" || !sessions.has(sessionId))
  )
    throw new TypeError("Invalid online pause proposal");
}
